import { Dayjs } from 'dayjs';
import { QualifyingAnswer } from '../utils/lead-queue-utils';
import { OpportunityTagModel } from './admin-rule-models';
import { FlowQuestion } from './qualifying-flow-models';

export interface OpportunitySearchResult {
  customerSalutation: string;
  customerFirstName: string;
  customerLastName: string;
  phoneNumber: string;
  email: string;

  internalId: string;
  signupId: string;
  globalCustomerId: string;
  opportunityId: string;
  bookingStatus: string | null;
  planningStatus: string | null;
  fullAddress: string;
  createdDate: Date;
  caseId: string;
  doNotCallBefore: Dayjs | null;

  externalSlotId: string | null;
  appointmentStartDate: Dayjs | null;
  appointmentStatus: string | null;
  salesFirstName: string | null;
  salesLastName: string | null;
  externalAppointmentId: string | undefined;
  cancellationReason: string | null;
  internalComments: string | null;

  assignedAgent: string | null;
  rescheduling: boolean | null;

  salesChannel: SalesChannel | null;
  isAutodialed: boolean;
}

export interface OpportunityAssignDto {
  internalId: string;
  /** True when assignment comes from the autodialer deep-link (/booking?type=autodialer&id=...). */
  isAutodialerFlow?: boolean;
}

export interface PreSalesApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  nextCursor?: string;
  httpStatusCode?: number;
}

export interface PreSalesCustomerInfo {
  customerId: string;
  personalDetails: PreSalesCustomerPersonalDetails;
  customerSource?: string;
  marketingSource?: string;
  isOwner?: string;
  houseType?: string;
  leadScore?: string;
  signupId?: string;
  gcid?: string;
  hpOrderNumber?: string;
  existingCustomerStatus?: string;
  dealDetails: PreSalesDealDetails;
}

export interface PreSalesAutodialerHpInfo {
  crmId?: string;
  customerInfo: PreSalesCustomerInfo;
  questionnaire: PreSalesQuestion[];
}

export interface PreSalesAdCase {
  caseInfo: CaseInfo;
  form: { formId: string; useCase: string; formValues: Record<string, string> } | null;
}

export interface PreSalesQuestionnaireContext {
  formId?: string;
  crmId?: string;
  hpOrderNumber?: string;
  signupId?: string;
  gcid?: string;
  customerSource?: string;
}

export interface PreSalesUnifiedSubmissionRequest {
  signupId: string;
  formId?: string;
  customerSource?: string;
  formValues: Array<{ questionKey: string; answer: string }>;
}

export interface PreSalesQualificationRequest {
  formId: string | null;
  formIdentifier: {
    crmId: string | null;
    signupId: string | null;
    hpOrderNumber: string | null;
  };
  formValues: Array<{ questionKey: string; answer: string }>;
}

export interface PreSalesQualification {
  formId: string;
  status: string;
  products: PreSalesQualificationProduct[];
}

export interface PreSalesQualificationProduct {
  product: string;
  status: 'Buildable' | 'NotBuildable' | 'NotInterested';
  companies: Array<{ company: string; status: string }>;
  cancellationReasons: Array<{
    reasonKey: string;
    source: string;
    company: string;
    message: string;
    pdReason: string;
    questionKeys: string[];
  }>;
}

export interface PreSalesCancellationRequest {
  hpOrderNumber: string;
  formValues: Record<string, string>;
  lastChangedKey?: string;
}

export interface PreSalesSaveFormRequest {
  formIdentifier: {
    crmId?: string;
    leadId?: string;
    hpOrderNumber?: string;
  };
  formValues: Record<string, string>;
}

export interface PreSalesFieldSalesEligibilityRequest {
  identifier: {
    crmId?: string;
    leadId?: string;
    hpOrderNumber?: string;
  };
  isFieldSalesEligible: boolean;
}

export interface PreSalesFormSubmissionRequest {
  crmId: string;
  answers: Array<Record<string, unknown>>;
  cancellation: PreSalesFormCancellation;
}

export interface PreSalesFormCancellation {
  isFormCancelled: boolean;
  cancellationReasons: PreSalesCancellationReason[];
}

export type PreSalesHandoverType = 'hp_to_heatx' | 'hp_to_pv';

export interface PreSalesHandoverRequest {
  product: 'pv';
  handoverType: PreSalesHandoverType;
  signupId: string;
  existingHandoverSignupId?: string;
  gcId?: string;
  identifier: {
    crmId: string;
    hpOrderNumber: string;
  };
  formValues: Record<string, string>;
  cancellation: PreSalesFormCancellation;
}

export interface PreSalesHandoverResult {
  signupId: string;
}

export interface PreSalesSubmitFormResponse {
  buildable: boolean;
  fieldSalesBookingAllowed: boolean;
  /** Upstream metadata. Autodialer HP booking follows the business rule that every buildable form can book. */
  showBooking: boolean;
  cancellationReasons: Record<string, string[]>;
  /** Unified (type=ad) flow only: eligible lanes ordered by priority. The first lane drives the initial calendar. */
  lanePriority?: string[] | null;
  bookingLinks?: {
    primary?: string | null;
    default?: string | null;
    primaryOwnerSlug?: string | null;
    defaultOwnerSlug?: string | null;
    primaryEventTypeSlug?: string | null;
    defaultEventTypeSlug?: string | null;
    /** Channel to record when booking through `primary`; owned by PreSales, never derived from the URL. */
    primaryChannel?: PreSalesBookingChannel | null;
    /** Channel to record when booking through `default`. */
    defaultChannel?: PreSalesBookingChannel | null;
  };
}

/** Unified-flow lane ids. See the backend `BookingLanes` constants. */
export const PreSalesBookingLanes = {
  HpVideo: 'hp_video',
  HpInVideo: 'hp_in_video',
  InVideo: 'in_video',
  PvVideo: 'pv_video',
  PvHpVideo: 'pv_hp_video',
  HpHv: 'hp_hv',
  HpVideoOverbooking: 'hp_video_overbooking',
} as const;

export type PreSalesBookingLane = (typeof PreSalesBookingLanes)[keyof typeof PreSalesBookingLanes] | string;

export type PreSalesBookingChannel = 'FieldSales' | 'VideoSales' | 'Insulation';

export interface PreSalesAvailabilityResult {
  available: boolean;
  agent?: PreSalesAvailabilityAgent | null;
  bookableAgent?: PreSalesAvailabilityAgent | null;
}

export interface PreSalesAvailabilityAgent {
  seller: string;
  salutation: string;
}

export interface PreSalesFieldSalesSlot {
  slotId: string;
  sellerName: string;
  start: string;
  end: string;
}

export interface PreSalesFieldSalesCalendarSlot extends CalendarSlot {
  endDate: Dayjs;
  fieldSalesSlots: PreSalesFieldSalesSlot[];
}

export interface PreSalesFieldSalesBookSlotRequest {
  dealId: string;
  slotId: string;
  zipCode: string;
}

export interface PreSalesFieldSalesBookSlotResult {
  slotId: string;
}

export interface PreSalesZeegBookSlotRequest {
  slotDate: string;
  name: string;
  email: string;
  ownerSlug?: string;
  eventTypeSlug?: string;
  customQueryParams?: Record<string, string>;
  /** Unified flow only: the lane the appointment is being booked in. */
  bookingLane?: string;
  /** Unified flow only: all lanes that were eligible, in priority order. */
  eligibleLanes?: string[];
  /** Unified flow only: why a non-Priority-1 lane was booked; absent when Priority-1 was booked. */
  bookingLaneReason?: string;
}

export interface PreSalesCancellationReason {
  product: string;
  message: string;
  company?: string;
  pdReason: string;
  questionKeys: string[];
}

export interface PreSalesQuestion {
  questionKey: string;
  questionText: string;
  type?: string;
  product?: string;
  products?: string[];
  order: number;
  answer?: string | number | string[];
  defaultValue?: string | number | null;
  visibleIf?: PreSalesVisibleIfCondition | null;
  validationRules?: PreSalesValidationRule[];
  warningChecks?: PreSalesWarningCheck[];
  additionalInfo?: PreSalesQuestionAdditionalInfo | null;
  [key: string]: unknown;
}

export interface PreSalesQuestionAdditionalInfo {
  isHeatLoadQuestion?: boolean | null;
  faqContent?: string | null;
}

export type PreSalesVisibleIfCondition =
  | { op: 'always' }
  | { op: 'never' }
  | { op: 'eq'; questionKey: string; value: string | number }
  | { op: 'or'; conditions: PreSalesVisibleIfCondition[] }
  | { op: 'and'; conditions: PreSalesVisibleIfCondition[] }
  | { op: 'not'; condition: PreSalesVisibleIfCondition }
  | { op: 'includes'; questionKey: string; value: string }
  | { op: 'in'; questionKey: string; values: (string | number)[] }
  | { op: 'arrayIncludes'; questionKey: string; value: string }
  | { op: 'arrayIncludesAny'; questionKey: string; values: string[] }
  | { op: 'arrayNotIncludes'; questionKey: string; value: string }
  | { op: 'eqExpression'; questionKey: string; expression: 'thresholdYear' }
  | { op: 'defined'; questionKey: string }
  | {
      op: 'prefixInRange';
      questionKey: string;
      from: number;
      to: number;
      length?: number;
    }
  | { op: 'numberInRange'; questionKey: string; from: number; to: number };

export type PreSalesValidationRule =
  | { type: 'required'; message: string }
  | { type: 'oneOf'; values: string[]; message: string }
  | { type: 'numberRange'; min: number; max: number; message?: string }
  | { type: 'gtField'; questionKey: string; message: string }
  | { type: 'requiredIf'; questionKey: string; value: string | string[]; message: string }
  | { type: 'requiredIfArrayIncludes'; questionKey: string; value: string; message: string }
  | { type: 'notAllowed'; value: string; message: string };

export type PreSalesWarningCheck =
  | { type: 'numberRange'; warnBelow?: number; warnAbove?: number; message: string }
  | { type: 'numberAbove'; warnAbove: number; message: string }
  | { type: 'numberBelow'; warnBelow: number; message: string };

export interface PreSalesCustomerPersonalDetails {
  firstName: string;
  lastName: string;
  email: string;
  address: string;
  street: string;
  housenumber: string;
  zipCode: string;
  city: string;
}

export interface PreSalesDealDetails {
  dealId: number;
  stageId: number;
  pipelineId: number;
  originalDealId: number;
}

export enum LeadTier {
  None = 'None',
  Gold = 'Gold',
  Silver = 'Silver',
  Bronze = 'Bronze',
  Wood = 'Wood',
  ThirdParty = 'ThirdParty',
  Copper = 'Copper',
}

export interface Calendar {
  lead: Lead;
  slots: CalendarSlot[];
  bookingLinks?: PreSalesSubmitFormResponse['bookingLinks'];
  calendarProvider?: 'Zeeg' | 'BookingOs' | 'FieldSales';
}

export interface Lead {
  gcid: string;
  opportunityId: string;
  salutation: string;
  firstName: string;
  lastName: string;
  tier: LeadTier;
  referredById: string | null;
  referredByName: string | null;
  products: string;
}

export interface CalendarSlot {
  startDate: Dayjs;
  dailyAvailableCount: number;
  salesChannel: SalesChannel;
  /** Field-sales slots only: the upstream slot id needed to book this exact slot. */
  externalSlotId?: string | null;
  /** Field-sales slots only: the real appointment end. On-site slots are not a fixed length. */
  endDate?: Dayjs | null;
  /** Field-sales slots only: the on-site agent the slot belongs to. */
  salesManagerName?: string | null;
}

export interface Slot {
  id: number;
  startDate: Dayjs;
  endDate: Dayjs;
  salesManagerName: string;
  externalType: string;
  externalStatus: string;
  meetingUrl: string;
  metaBookingInfo: string;
  externalId: string;
}

export interface SalesManagerAvailability {
  id: number;
  name: string;
  dailyBookCount: number;
  salesLeague: number;
}

export enum AppointmentType {
  None = 0,
  Slot = 1,
  SalesCall1 = 2,
  SalesCall2 = 3,
}

interface Pagination {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface Result<T> {
  data: T;
  errors: string[] | null;
  isSuccess: boolean;
}

export interface PaginatedResult<T> extends Result<T> {
  pagination: Pagination;
}

export interface SlotConflict {
  slotId: number;
  slotDate: string;
  conflictingSlotId: number;
  conflictingSlotSubject: string;
  salesManagerId: number;
  salesManagerName: string;
  salesManagerLeague: number;
  salesManagerTeamName: string;
  slotUrl: string;
  slotTier: string;
  bookedOpportunityId: string;
}

export interface CaseInfo {
  internalId: string;
  processingType: string;
  globalCustomerId: string;
  opportunityId: string;
  signupId: string;
  caseId: string;
  referrer: string;
  contactId: string;
  salutation: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  leadPhoneNumber: string;
  leadPhoneId: string | null;
  street: string;
  houseNumber: string;
  city: string;
  zipcode: string;
  bookingStatus: string;
  leadTier: LeadTier;
  paymentModel: string;
  productType: string;
  caseComments: CaseComments[];
  callCounter: number;
  notReachedCounter: number;
  doNotCallBefore: Date | null;
  salesScript: string;
  salesObjections: OpportunityObjection[];
  salesChannel: SalesChannel | null;
  expireAt: Date | null;
  createdDate: Date | null;
  leadSource: string;
  aBTestingFragment: string;
  opportunityTags: OpportunityTagModel[];
  qualifyingQuestions: FlowQuestion[];
  isFieldSalesEligible: boolean;
  showQualifyingQuestions: boolean;
  isAutodialed: boolean;
  configurableLostReasonsInBooking?: boolean;
}

export const defaultCaseInfo: CaseInfo = {
  internalId: '',
  processingType: '',
  globalCustomerId: '',
  opportunityId: '',
  signupId: '',
  caseId: '',
  referrer: '',
  contactId: '',
  salutation: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  leadPhoneNumber: '',
  leadPhoneId: null,
  street: '',
  houseNumber: '',
  city: '',
  zipcode: '',
  bookingStatus: '',
  leadTier: LeadTier.None,
  productType: '',
  paymentModel: '',
  caseComments: [],
  callCounter: 0,
  notReachedCounter: 0,
  doNotCallBefore: null,
  salesScript: '',
  salesObjections: [],
  salesChannel: null,
  expireAt: null,
  createdDate: null,
  leadSource: '',
  aBTestingFragment: '',
  opportunityTags: [{ feId: crypto.randomUUID(), color: '', value: '' }],
  qualifyingQuestions: [],
  isFieldSalesEligible: false,
  showQualifyingQuestions: false,
  isAutodialed: false,
  configurableLostReasonsInBooking: false,
};

export interface CaseComments {
  body: string;
  createdBy: string;
  createdOn: Date;
  isCustomerLostReason: boolean;
  looseReasons: string;
  isNotBookedReason: boolean;
}

export interface OpportunityComplete {
  id: string;
  opportunityId: string;
  globalCustomerId: string;
  caseId: string;
  signUpId: string;
  opportunityComments: string;
  doNotCallBefore: Date | null;
  customerLostReasons: string[] | null;
  customerLostDescription: string | null;
  caseStatus: CaseStatus;
  mailbox: boolean;
  shareContactData?: boolean | null;
  isDataDeletionRequested: boolean;
  qualifyingAnswers?: QualifyingAnswer[];
  notBookedReason?: string;
  wrongContactId?: string;
  callSource?: string;
  /** Start/end of the SC1 appointment booked via Zeeg (unified flow only). */
  bookingStartDate?: string | Dayjs;
  bookingEndDate?: string | Dayjs | null;
  /** The unified-flow lane the appointment was booked in; the backend derives its product/sales-channel. */
  bookingLane?: string;
  /** Why the agent booked outside the first offered lane; absent when the first lane was booked. */
  bookingLaneReason?: string;
}

export interface CaseAssignment {
  caseId: string;
  userEmail: string;
  expireAt: Date | null;
}

export interface TotalOpportunities {
  count: number;
  opportunities: CaseInfo[];
}

export enum CaseStatus {
  None = 0,
  Booked = 1,
  ReachedButNotBooked = 2,
  NotReached = 3,
  CustomerLost = 4,
  WrongNumber = 5,
}

export interface BulkUploadCase {
  cases: string[];
  overrides: {
    callCount: string | undefined;
  };
}

export enum SalesChannel {
  MissingQueue = 'MissingQueue',
  DigitalSales = 'DigitalSales',
  SalesOS = 'SalesOS',
  CrmSales = 'CrmSales',
  Revision = 'Revision',
  SalesCall2 = 'SalesCallTwo',
  FieldSales = 'FieldSales',
  LeadQueue = 'LeadQueue',
  SalesCall1Lead = 'SalesCall1Lead',
  HeatSales = 'HeatSales',
  Retention = 'Retention',
  SpecialSalesChannel1 = 'SpecialSalesChannel1',
  SpecialSalesChannel2 = 'SpecialSalesChannel2',
  SpecialSalesChannel3 = 'SpecialSalesChannel3',
  SpecialSalesChannel4 = 'SpecialSalesChannel4',
  SpecialSalesChannel5 = 'SpecialSalesChannel5',
}

export enum ProductType {
  UNKNOWN = 'UNKNOWN',
  PV = 'PV',
  ZEUS = 'ZEUS',
  COMBINED = 'COMBINED',
  HEATPUMP = 'HEATPUMP',
}

export enum PaymentModel {
  ANY = 'Any',
  LEASE = 'Lease',
  LOAN = 'Loan',
}

export enum Department {
  NONE = 'NONE',
  CustomerService = 'Customer Service',
  IDNow = 'IDNow',
  HomeImprovement = 'Home Improvement',
  SalesBerlin = 'Sales Berlin',
  SalesEssen = 'Sales Essen',
  SalesFrankfurt = 'Sales Frankfurt',
  SalesHamburg = 'Sales Hamburg',
  SalesLeipzig = 'Sales Leipzig',
  SalesMunchen = 'Sales München',
  SalesRetention = 'Sales Retention',
}

export enum SalesLeague {
  Zero = 0,
  One = 1,
  Two = 2,
  Three = 3,
  Four = 4,
  Five = 5,
  Six = 6,
  Seven = 7,
  Eight = 8,
  Nine = 9,
  Ten = 10,
  Eleven = 11,
  Twelve = 12,
  Thirteen = 13,
  Fourteen = 14,
}

export interface OpportunityEditContactDto {
  opportunityId: string;
  signUpId: string;
  globalCustomerId: string;
  contactId: string;
  firstName: string;
  oldFirstName?: string;
  lastName: string;
  oldLastName?: string;
  email: string;
  oldEmail?: string;
  phone: string;
  salutation: string;
  oldSalutation?: string;
  street: string;
  oldStreet?: string;
  houseNumber: string;
  oldHouseNumber?: string;
  city: string;
  oldCity?: string;
  zipCode: string;
  oldZipCode?: string;
}

export interface CustomerDeleteData {
  customerFullName: string;
  bookingAgentFullName: string;
  contactUrl: string;
}

export interface SalesManager {
  id: number;
  lastModifiedDate: Date | null;
  lastModifiedBy: string;
  isDeleted: boolean;
  firstName: string;
  lastName: string;
  salesLeague: SalesLeague;
  productType: ProductType;
  externalAccountId: string;
  email: string;
  teamName: string;
  isTeamLead: boolean;
  paymentModel: PaymentModel;
  azureId: string;
  regions: string[];
  salesChannel: SalesChannel;
  department: Department;
}

export interface GroupSlotData {
  externalAppointmentId: string;
  appointmentTypeId: number;
  startDateTimeUtc: Date;
  productType: string;
  paymentModel: string;
  salesManagerCount: number;
  isOverbookSlot: boolean;
}

export interface BaseCardModel {
  name: string;
  description: string;
}

export interface ScriptsWithObjections extends BaseCardModel {
  name: string;
  scripts: OpportunityScript[];
  objections: OpportunityObjection[];
}

export interface OpportunityScript {
  id: number;
  name: string;
  bookingStatus: string;
  scriptText: string;
  lastModifiedDate: Date;
  lastModifiedBy: string;
  isDeleted: boolean;
}

export interface OpportunityObjection {
  id: number;
  scriptName: string;
  objection: string;
  answer: string;
  sortOrder: number;
  lastModifiedDate: Date;
  lastModifiedBy: string;
  isDeleted: boolean;
}

export type CommonEditComponentProps<T> = {
  model: T;
  onSubmit: (form: T) => void;
  onCancel: () => void;
};

export enum BookingStatus {
  FirstTimeScheduling = 'First Time Scheduling',
  ReschedulingNeeded = 'Rescheduling Needed',
}

export type UserCustomizableUI = {
  selectedBookingButton: string | undefined;
};

export interface EditContact {
  id: number;
  opportunityId: string;
  contactId: string;
  firstName: string;
  oldFirstName?: string | null;
  lastName: string;
  email: string;
  oldEmail?: string | null;
  oldLastName?: string | null;
  city?: string | null;
  oldCity?: string | null;
  street?: string | null;
  oldStreet?: string | null;
  houseNumber?: string | null;
  oldHouseNumber?: string | null;
  zipCode?: string | null;
  oldZipCode?: string | null;
  salutation?: string | null;
  oldSalutation?: string | null;
  contactLink: string;
  opportunityLink: string;
  isCompleted: boolean;
  createdAtUtc: Date;
  modifiedAtUtc: Date;
}

export interface StartCallResponseBody {
  callId: string;
}

export interface CallBodyRequest {
  customerPhoneNumber: string;
  customerId: string;
}

export type SalesCall1LeadContact = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
};

export type SalesCall1LeadContactInfo = { contact: SalesCall1LeadContact; globalCustomerId: string; contactId: string };
